import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMutation } from '@tanstack/react-query'
import { errorMessage, useTRPC } from '@/lib/trpc'
import { formatMarketCapRange, formatUsdShorthand, parseUsdShorthand } from '@/lib/format'
import { Modal } from './modal'
import { NumericInput } from './numeric-input'

/** what the dialog needs of a strategy: its id and the saved range (a list row or the detail view both carry it) */
export type MarketCapTarget = { id: string; limits: { minMarketCapUsd: number | null; maxMarketCapUsd: number | null } }

/**
 * The market cap range an order must sit inside, changed after creation (John, Nov 2 2026): the pencil on a row
 * opens it, the figures take shorthand like 2.5m or 600k, and both left empty means any market cap.
 */
export function MarketCapDialog({ strategy: s, open, onClose, onSaved }: { strategy: MarketCapTarget; open: boolean; onClose: () => void; onSaved: () => void }) {
  const trpc = useTRPC()
  const [min, setMin] = useState('')
  const [max, setMax] = useState('')
  const [error, setError] = useState<string | null>(null)
  const save = useMutation(
    trpc.strategy.setMarketCap.mutationOptions({
      onSuccess: () => {
        onSaved()
        onClose()
      },
      onError: (e) => setError(errorMessage(e)),
    }),
  )
  const savedMin = s.limits.minMarketCapUsd
  const savedMax = s.limits.maxMarketCapUsd
  // the inputs start from the saved range each time the dialog opens
  useEffect(() => {
    if (!open) return
    setMin(savedMin === null ? '' : formatUsdShorthand(savedMin))
    setMax(savedMax === null ? '' : formatUsdShorthand(savedMax))
    setError(null)
  }, [open, savedMin, savedMax])
  const minUsd = parseUsdShorthand(min)
  const maxUsd = parseUsdShorthand(max)
  const unreadable = minUsd === null || maxUsd === null
  function submit() {
    setError(null)
    if (unreadable) return setError('Enter the market cap as a figure, like 2.5m or 600k.')
    if (minUsd !== undefined && maxUsd !== undefined && minUsd >= maxUsd) return setError('The market cap minimum must be below the maximum.')
    save.mutate({ id: s.id, minMarketCapUsd: minUsd ?? null, maxMarketCapUsd: maxUsd ?? null })
  }
  // portaled to the body: a dialog inside the detail card would be clipped by its overflow
  return createPortal(
    <Modal
      open={open}
      onClose={onClose}
      title="Market cap"
      width="sm"
      footer={
        <div className="flex gap-3">
          <button type="button" className="btn btn-secondary flex-1" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary flex-1" disabled={save.isPending || unreadable} onClick={submit}>
            Save
          </button>
        </div>
      }
    >
      <form
        className="grid gap-4 text-sm"
        onSubmit={(event) => {
          event.preventDefault()
          submit()
        }}
      >
        <p className="text-xs text-muted">
          Orders only run while the token's market cap is inside the range; otherwise they are held back and retried at the next interval. Leave both empty for any market cap.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <MarketCapInput label="Minimum" value={min} onChange={setMin} />
          <MarketCapInput label="Maximum" value={max} onChange={setMax} />
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-muted">Orders run</span>
          <span className="text-right tabular-nums">{unreadable ? 'Enter a figure like 2.5m or 600k' : formatMarketCapRange(minUsd, maxUsd)}</span>
        </div>
        {error ? <p className="break-words text-negative">{error}</p> : null}
        <button type="submit" className="sr-only">
          Save
        </button>
      </form>
    </Modal>,
    document.body,
  )
}

/** one bound of the range: a dollar sign, then the figure as typed */
function MarketCapInput({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="grid gap-1.5">
      <span className="text-xs text-muted">{label}</span>
      <div className="control flex items-center px-3">
        <span className="text-muted">$</span>
        <NumericInput
          mode="shorthand"
          className="w-full bg-transparent py-2 pl-1 text-base outline-none sm:text-sm"
          placeholder="e.g. 2.5m"
          value={value}
          onChange={onChange}
        />
      </div>
    </label>
  )
}
